import { Tooltip, Typography } from "@mui/joy";
import { Box } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { SECONDS_IN_A_DAY } from "../constants";
import { Timer } from "../entities";
import { useKronosTasks } from "../../redux/kronos/kronosHooks";

const heights = {
  sm: 12,
  md: 16,
  lg: 20,
};

/**
 *
 * @returns {ReactElement} KronosDayLine page
 */
export function KronosDayLine({
  timers,
  date,
  size = "md",
  steps = true,
}: KronosDayLineType) {
  const [key, setKey] = useState<number>(0);
  const now = moment();
  const mDate = moment(date);
  const startOfDay = moment(date).startOf("day");
  const endOfDay = moment(date).endOf("day");
  const { tasks } = useKronosTasks();
  const [_timers, setTimers] = useState<Timer[]>([]);

  const steppers = [
    moment(date).startOf("day").add(6, "hour"),
    moment(date).startOf("day").add(12, "hour"),
    moment(date).startOf("day").add(18, "hour"),
  ];

  let refreshInterval: ReturnType<typeof setInterval> | null;

  useEffect(() => {
    clear();
    if (timers) {
      if (!refreshInterval) {
        refreshInterval = setInterval(() => setKey((k) => k + 1), 10000);
      }

      setTimers(
        timers.filter(
          (t) =>
            moment(t.startDateUTC).isSame(mDate, "day") ||
            (t.endDateUTC && moment(t.endDateUTC).isSame(mDate, "day"))
        )
      );
    }

    return clear;
  }, [timers]);

  const clear = () => {
    if (refreshInterval) clearInterval(refreshInterval);
    refreshInterval = null;
  };

  const getOffset = (_date: string): string => {
    const d = moment(_date).diff(startOfDay, "second");
    const percent = (d / SECONDS_IN_A_DAY) * 100;
    return `${percent}%`;
  };

  const getWidth = (_seconds: number): string => {
    const percent = (_seconds / SECONDS_IN_A_DAY) * 100;
    return `${percent}%`;
  };

  return (
    <Box sx={{ position: "relative", height: 40 }} key={key}>
      <Box
        sx={{
          height: heights[size],
          background: "#E0E6F0",
          borderRadius: 4,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {_timers.map((t) => {
          const task = tasks.find((ta) => ta.id === t.taskID);
          return (
            <Tooltip title={t.label(date)} arrow size="sm" key={t.id}>
              <Box
                sx={{
                  background: task?.color ?? "#2196f3",
                  height: "100%",
                  borderRadius: 2,
                  left: t.offset(date),
                  width: t.width(date),
                  position: "absolute",
                  right: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {task ? (
                  <Typography level="body5" noWrap>
                    {task.name}
                  </Typography>
                ) : (
                  []
                )}
              </Box>
            </Tooltip>
          );
        })}
        {mDate.isSameOrAfter(now, "day") ? (
          <Tooltip title="Future" arrow size="sm">
            <Box
              sx={{
                background: "#B1BBC9",
                height: "100%",
                left: getOffset(moment().utc().format()),
                width: getWidth(mDate.diff(endOfDay)),
                position: "absolute",
                right: 0,
              }}
            />
          </Tooltip>
        ) : (
          []
        )}
      </Box>
      {steps &&
        steppers.map((s, i) => (
          <Box
            key={i}
            sx={{
              background: "#B1BBC9",
              height: 5,
              left: getOffset(s.format()),
              width: "1px",
              position: "absolute",
              right: 0,
            }}
          >
            <Box sx={{ width: 100, mt: "5px" }}>
              <Typography level="body4">{s.format("hh A")}</Typography>
            </Box>
          </Box>
        ))}
    </Box>
  );
}

type KronosDayLineType = {
  timers?: Timer[];
  date?: string;
  steps?: boolean;
  size?: "sm" | "md" | "lg";
};
